import { PageTitle, Btn, Card, Spinner } from "../components";
import { useCallback, useEffect, useMemo, useState } from "react";
import { apiFetch } from "../api";
import { useTenant } from "../context/TenantContext";
import { recordUiError } from "../observability";

export default function TenantBillingPage() {
  const tenant = useTenant();
  const [billing, setBilling] = useState(null);
  const [refresh, setRefresh] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const showError = useCallback((message) => {
    recordUiError({ message, page: "billing", source: "inline_error" });
    setError(message);
  }, []);

  useEffect(() => {
    setLoading(true);
    setError("");
    apiFetch("/tenant/billing")
      .then(setBilling)
      .catch((e) => showError(e.message))
      .finally(() => setLoading(false));
  }, [showError, refresh]);

  const featureLabelMap = useMemo(() => Object.fromEntries(
    (billing?.feature_catalog || []).map((item) => [item.key, item.label])
  ), [billing?.feature_catalog]);

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        <Spinner />
      </div>
    );
  }

  if (error) {
    return <Card style={{ padding: 24 }}><p role="alert" style={{ color: "var(--danger)", marginBottom: 12 }}>{error}</p><Btn variant="secondary" onClick={() => setRefresh(value => value + 1)}>Tentar novamente</Btn></Card>;
  }

  const usage = billing?.usage?.routes_per_month || tenant.usage?.routes_per_month || {};
  const limitLabel = usage.limit == null ? "Ilimitado" : usage.limit;
  const features = billing?.features || tenant.features || {};
  const featureEntries = Object.entries(features);
  const missingCoreFeatures = !features.routes || !features.documents;

  return (
    <div className="fade-in" style={{ display: "grid", gap: 16, maxWidth: 720 }}>
      <div>
        <PageTitle icon="card">Plano e uso</PageTitle>
        <p style={{ color: "var(--muted)", fontSize: '1rem' }}>
          {billing?.plan_name || tenant.plan_name || "—"} ({billing?.plan_code || tenant.plan_code || "—"})
        </p>
      </div>

      <Card style={{ padding: 20, display: "grid", gap: 12 }}>
        <div style={{ fontWeight: 600 }}>Rotas enviadas no mês</div>
        <div style={{ fontSize: '1.75rem', fontWeight: 700, fontFamily: "var(--font-mono)" }}>
          {usage.used ?? 0}
          <span style={{ fontSize: '1rem', color: "var(--muted)", fontWeight: 500 }}> / {limitLabel}</span>
        </div>
      </Card>

      <Card style={{ padding: 20 }}>
        <div style={{ fontWeight: 600, marginBottom: 10 }}>Recursos do plano</div>
        {missingCoreFeatures && (
          <div style={{
            marginBottom: 12,
            padding: "10px 12px",
            borderRadius: 8,
            background: "rgba(249,115,22,0.12)",
            border: "1px solid rgba(249,115,22,0.25)",
            color: "var(--text)",
            fontSize: '0.9375rem',
            lineHeight: 1.5,
          }}>
            Seu plano não inclui o planejamento de rotas ou a gestão de documentos.
            Fale com o suporte RotaFlex para consultar os planos disponíveis.
          </div>
        )}
        {featureEntries.length === 0 ? (
          <p style={{ margin: 0, color: "var(--muted)", fontSize: '0.9375rem' }}>
            Nenhum recurso configurado para este plano. Entre em contato com o suporte.
          </p>
        ) : (
        <ul style={{ margin: 0, paddingLeft: 18, color: "var(--muted)", lineHeight: 1.8 }}>
          {featureEntries.map(([key, enabled]) => (
            <li key={key}>
              <span style={{ color: enabled ? "var(--success)" : "var(--danger)" }}>
                {enabled ? "✓" : "✗"}
              </span>{" "}
              {featureLabelMap[key] || key}
            </li>
          ))}
        </ul>
        )}
      </Card>
    </div>
  );
}
